// ---------------------------------------
//       ALM Export Vintage Method
// ---------------------------------------

import { styleRangeBasedOnCalibrationStatus } from '@components/export/spread/helpers';
import * as GC from '@grapecity/spread-sheets';
import { IALMForecast } from '@models/export-alm/alm-forecast';
import { ILookupProductBetaVintage } from '@models/export-alm/lookup-product-beta';
import { CalibrationStatus } from '@models/record-status';
import { flat } from '@shared/arrays';
import { getLastDayOfNextMonth } from '@shared/dates';
import { tryParseNumber } from '@shared/numbers';
import {
  borders,
  colours,
  CURRENCY_FORMATTER,
  DATE_FORMATTER,
  FONT_NAME,
  FONT_SIZE,
  PERCENTAGE_FORMATTER
} from '@shared/spread/spread-settings';
import { baseSheets, configureSheet } from './export-alm-spread';

const cpTableHeaders = [
  'SecID',
  'AccountNum',
  'DepProductMn',
  'VintageType',
  'IssueDate',
  'OriginalBook',
  'AvgBook',
  'EOPBook',
  'CurrCoupon',
  'FTPBeta'
];

export const getSheet_lookupsVintage = (lookups: ILookupProductBetaVintage[]): GC.Spread.Sheets.Worksheet => {
  // Create Sheet
  const sheet = new GC.Spread.Sheets.Worksheet(baseSheets[2].name); // 'Lookup Tables'

  sheet.suspendPaint();
  configureSheet(sheet, baseSheets[2]);

  const numPeriods = Math.max(...lookups.map(l => l.RunOff).map(ro => ro.DecayFactors.length));

  setStyling_lookupsVintage(sheet, lookups, numPeriods);

  //   BETA LOOKUPS
  sheet.getCell(1, 1).text('Lookups');
  sheet.getCell(2, 1).text('NMD_Betas');
  const productMnemonicsRate = lookups.map(l => `${l.ProductMnemonic.toString()}_Rate`);
  const betaCoefficients = lookups.map(l => l.BetaCoefficient);
  sheet.setArray(3, 1, productMnemonicsRate);
  sheet.setArray(3, 2, betaCoefficients);

  // Adding Named Range For Beta Look up
  sheet.clearSelection();
  sheet.addSelection(1, 1, 2 + productMnemonicsRate.length, 2);
  const betaLookUpFormula = GC.Spread.Sheets.CalcEngine.rangeToFormula(sheet.getSelections()[0]);
  sheet.addCustomName('EMP_NMD_Betas', `'${sheet.name()}'!${betaLookUpFormula}`, 1, 1, '');
  sheet.clearSelection();

  //   DECAY FACTORS LOOKUPS
  sheet.getCell(lookups.length + 4, 1).text('Lookups');
  sheet.getCell(lookups.length + 5, 1).text('NMD_DecayFactors');

  const productMnemonics = lookups.map(l => `${l.ProductMnemonic}`);
  sheet.setArray(lookups.length + 7, 1, productMnemonics);

  const periods = new Array(numPeriods).fill(null).map((e, i) => i);
  sheet.setArray(lookups.length + 6, 2, [periods]);

  lookups.forEach((lookup, i) => {
    if (lookup.RunOff) {
      const cashFlows = lookup.RunOff.DecayFactors.map(dfItem => dfItem.Factor);
      sheet.setArray(lookups.length + 7 + i, 2, [cashFlows]);
    }
  });

  // Adding Named Ranges for Runoff SMM table
  sheet.addSelection(lookups.length + 4, 1, 3 + lookups.length, periods.length + 1);
  const runOffLookupFormula = GC.Spread.Sheets.CalcEngine.rangeToFormula(sheet.getSelections()[0]);
  sheet.addCustomName('EMP_NMD_DecayFactors', `'${sheet.name()}'!${runOffLookupFormula}`, lookups.length + 4, 1, '');
  sheet.clearSelection();

  // Styling based on Calibration Status
  lookups.forEach((lookup, i) => {
    if (lookup.CalibrationStatus !== 'A') {
      styleRangeBasedOnCalibrationStatus(sheet.getRange(3 + i, 2, 1, 1), lookup.CalibrationStatus as CalibrationStatus);
    }

    // <9377> RunOffs styled based on Decay and Stable Balance calibrations' statuses
    const { DecayCalibrationStatus } = lookup.RunOff;
    if (DecayCalibrationStatus !== 'A') {
      const range = sheet.getRange(lookups.length + 7 + i, 2, 1, numPeriods);

      if (DecayCalibrationStatus === 'R') {
        range.backColor(colours.REJECTED_BG);
      } else {
        range.backColor(colours.PROPOSED_BG);
      }
    }
  });

  return sheet;
};

const setStyling_lookupsVintage = (sheet: GC.Spread.Sheets.Worksheet, lookups: ILookupProductBetaVintage[], numPeriods: number) => {
  sheet.setRowCount(Math.max(30, 9 + 2 * lookups.length));
  sheet.setColumnCount(Math.max(14, numPeriods + 3));

  // Clear previous data and styling
  const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
  fullRange.clear(GC.Spread.Sheets.StorageType.style);
  fullRange.clear(GC.Spread.Sheets.StorageType.data);

  sheet.setColumnWidth(0, 30);

  sheet.frozenColumnCount(2);
  sheet.options.frozenlineColor = 'transparent';

  // -------------------------------------------------------------------------------------
  //   BETA LOOKUPS
  // -------------------------------------------------------------------------------------
  const tableRange = sheet.getRange(3, 1, lookups.length, 2);
  const titleRange = sheet.getRange(1, 1, 1, 1);
  const subtitleRange = sheet.getRange(2, 1, 1, 1);
  // const productsRange = sheet.getRange(3, 1, lookups.length, 1);

  fullRange
    .locked(true)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`)
    .wordWrap(true)
    .font(`${parseInt(FONT_SIZE, 10)}pt ${FONT_NAME}`);

  titleRange
    .backColor(colours.Lookups.mnemonicBackground)
    .font(`bold ${Number(FONT_SIZE) + 3}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
  subtitleRange
    .backColor(colours.Lookups.thirdDimensionBackground)
    .font(`${Number(FONT_SIZE) + 1}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { left: true, right: true });

  for (let c = 0; c < lookups.length; c++) {
    sheet.getCell(3 + c, 1).backColor(c % 2 === 0 ? colours.Lookups.columnLightBackground : colours.Lookups.columnBackground);
    sheet.getCell(3 + c, 2).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
  }

  tableRange.setBorder(borders.BLACK_THIN, { outline: true });

  // -------------------------------------------------------------------------------------
  //   DECAY FACTORS LOOKUPS
  // -------------------------------------------------------------------------------------
  const tableRangeDF = sheet.getRange(lookups.length + 6, 1, lookups.length + 1, numPeriods + 1);
  const titleRangeDF = sheet.getRange(lookups.length + 4, 1, 1, 1); // Look ups
  const subtitleRangeDF = sheet.getRange(lookups.length + 5, 1, 1, 1); // NMD_DecayFactors
  // const productsRangeDF = sheet.getRange(lookups.length + 7, 1, lookups.length, 1);
  const periodsRange = sheet.getRange(lookups.length + 6, 2, 1, numPeriods);
  const runOffsRange = sheet.getRange(lookups.length + 7, 2, lookups.length, numPeriods);

  titleRangeDF
    .backColor(colours.Lookups.mnemonicBackground)
    .font(`bold ${Number(FONT_SIZE) + 3}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
  subtitleRangeDF
    .backColor(colours.Lookups.thirdDimensionBackground)
    .font(`${Number(FONT_SIZE) + 1}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { left: true, right: true });

  periodsRange.backColor(colours.Lookups.columnLightBackground);

  runOffsRange.formatter(PERCENTAGE_FORMATTER).hAlign(GC.Spread.Sheets.HorizontalAlign.right);

  for (let c = 0; c < lookups.length; c++) {
    sheet
      .getCell(lookups.length + 7 + c, 1)
      .backColor(c % 2 === 0 ? colours.Lookups.columnLightBackground : colours.Lookups.columnBackground);

    sheet.getRange(lookups.length + 7 + c, 2, 1, numPeriods).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
  }

  tableRangeDF.setBorder(borders.BLACK_THIN, { outline: true });
};

export const getSheet_VintageCPTables = (
  lookups: ILookupProductBetaVintage[],
  userFieldColumns: string[],
  numberOfSegments: number
): GC.Spread.Sheets.Worksheet => {
  // Create Sheet
  const sheet = new GC.Spread.Sheets.Worksheet(baseSheets[3].name); // 'Vintage CP Table'

  sheet.suspendPaint();
  sheet.suspendEvent();
  sheet.suspendCalcService();

  configureSheet(sheet, baseSheets[3]);

  const allHeaders = [...cpTableHeaders, ...userFieldColumns];

  setStyling_VintageCPTables(sheet, allHeaders, numberOfSegments);

  // Set headers using static column names
  sheet.setArray(0, 0, [allHeaders]);

  let i = 1;

  lookups.forEach((lookup, l) => {
    if (lookup.Segments.length > 0) {
      for (let s = 0; s < lookup.Segments.length; s++, i++) {
        const segment = lookup.Segments[s];
        const { SecID, AccountNum, DepProductMn, VintageType, IssueDate, OriginalBook, AvgBook, EOPBook, CurrCoupon, FTPBeta } = segment;

        const row = [
          SecID,
          AccountNum,
          DepProductMn,
          VintageType,
          new Date(IssueDate),
          OriginalBook,
          AvgBook,
          EOPBook,
          CurrCoupon,
          FTPBeta
        ];

        userFieldColumns.forEach(key => {
          row.push(tryParseNumber(segment.CustomFields[key]));
        });

        sheet.setArray(i, 0, [row]);
      }

      if (numberOfSegments < 10000 && l < lookups.length - 1) {
        // Horizontal product borders
        const range = sheet.getRange(i - 1, 0, 1, sheet.getColumnCount());
        range.setBorder(borders.BLACK_THIN, { bottom: true });
      }
    }
  });

  sheet.setColumnWidth(0, 370); // SecID column
  sheet.setColumnWidth(1, 100); // AccountNum column
  sheet.setColumnWidth(2, 200); // DepProductMn column
  sheet.setColumnWidth(3, 100); // VintageType column
  sheet.setColumnWidth(4, 90); // IssueDate column
  sheet.setColumnWidth(8, 100); // CurrCoupon column
  sheet.setColumnWidth(9, 80); // FTPBeta column

  return sheet;
};

export const getSheet_VintageCPTables_Warning = () => {
  const sheet = new GC.Spread.Sheets.Worksheet(baseSheets[3].name); // 'Vintage CP Table'

  const warning1 = 'Current position export has too many rows.';
  const warning2 = 'All rows have been saved to the CP_Data database table.';

  sheet.suspendPaint();
  sheet.suspendEvent();
  sheet.suspendCalcService();

  configureSheet(sheet, baseSheets[3]);

  sheet.setRowCount(30);
  sheet.setColumnCount(10);

  const fullRange = sheet.getRange(0, 0, 30, 10);
  fullRange
    .locked(true)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10)}`)
    .font(`14pt ${FONT_NAME}`);

  sheet.setValue(1, 1, warning1);
  sheet.setValue(2, 1, warning2);

  sheet.setColumnWidth(0, 50);

  return sheet;
};

const setStyling_VintageCPTables = (sheet: GC.Spread.Sheets.Worksheet, allHeaders: string[], numOfSegments: number) => {
  const columnCount = allHeaders.length;
  sheet.setRowCount(Math.max(31, numOfSegments + 1));
  sheet.setColumnCount(columnCount);

  // Clear previous data and styling
  const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), columnCount);
  fullRange.clear(GC.Spread.Sheets.StorageType.style);
  fullRange.clear(GC.Spread.Sheets.StorageType.data);

  sheet.frozenRowCount(1);
  sheet.options.frozenlineColor = 'transparent';

  const headerRange = sheet.getRange(0, 0, 1, columnCount);
  headerRange
    .backColor(colours.Lookups.rowBackground)
    .font(`bold ${Number(FONT_SIZE) + 1}pt ${FONT_NAME}`)
    .setBorder(borders.ALM_EXPORT_TABLE, { outline: true });

  // Minimal styling if over 10k rows
  if (numOfSegments > 10000) {
    return;
  }

  fullRange
    .locked(true)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`)
    .wordWrap(true)
    .font(`${parseInt(FONT_SIZE, 10)}pt ${FONT_NAME}`);

  const bodyRange = sheet.getRange(1, 0, numOfSegments, columnCount);
  bodyRange.setBorder(borders.ALM_EXPORT_TABLE, { outline: true });

  const currCouponRange = sheet.getRange(1, cpTableHeaders.indexOf('CurrCoupon'), numOfSegments, 1);
  currCouponRange.formatter(PERCENTAGE_FORMATTER).hAlign(GC.Spread.Sheets.HorizontalAlign.right);

  const eopDateRange = sheet.getRange(1, cpTableHeaders.indexOf('IssueDate'), numOfSegments, 1);
  eopDateRange.formatter(DATE_FORMATTER);

  for (let c = 1; c < numOfSegments; c += 2) {
    sheet.getRange(1 + c, 0, 1, columnCount).backColor(colours.WHITE2);
  }

  // Vertical Borders
  allHeaders.forEach((header, i) => {
    const range = sheet.getRange(0, i, numOfSegments + 1, 1);
    const blackBorder: boolean = i === cpTableHeaders.length - 1 && allHeaders.length > cpTableHeaders.length;
    range.setBorder(blackBorder ? borders.BLACK_THIN : borders.ALM_EXPORT_TABLE, { right: true });
  });
};

export const getSheet_Forecasts = (forecasts: IALMForecast[]): GC.Spread.Sheets.Worksheet => {
  const sheet = new GC.Spread.Sheets.Worksheet(baseSheets[4].name); // 'Forecasts'

  if (sheet && forecasts.length > 0 && forecasts[0].TimeSeries.length > 0 && forecasts[0].TimeSeries[0].StableBalance.length > 0) {
    configureSheet(sheet, baseSheets[4]);

    sheet.setColumnCount(forecasts[0].TimeSeries[0].TotalBalance.length + 5);

    let numForecasts = 0;
    forecasts.forEach(x => {
      numForecasts += x.TimeSeries.length * 2;
    });

    sheet.setRowCount(numForecasts + 8);

    let rowIndex = 0;
    // Setting Headers for total balance
    rowIndex++;
    sheet.getCell(rowIndex, 1).text('Mnemonic');
    sheet.getCell(rowIndex, 2).text('BSTargets');
    sheet.getCell(rowIndex, 3).text('Entity');
    sheet.getCell(rowIndex, 4).text('Target Method');
    sheet.setArray(rowIndex, 5, [new Array(forecasts[0].TimeSeries[0].TotalBalance.length).fill(0).map((val, ind) => (val = ind))]);

    // Total Balance Loop
    forecasts.forEach((forecast, index) => {
      forecast.TimeSeries.forEach(timeSeries => {
        rowIndex++;
        sheet.getCell(rowIndex, 1).text(timeSeries.Mnemonic);
        sheet.getCell(rowIndex, 2).text(String(timeSeries.AccountNum));
        sheet.getCell(rowIndex, 3).text(timeSeries.EntityID !== null ? timeSeries.EntityID : '');
        sheet.getCell(rowIndex, 4).text('Average');
        sheet.setArray(rowIndex, 5, [timeSeries.TotalBalance.map(({ Balance }) => Balance)]);
      });
    });
    rowIndex += 3;
    // Total Balance Named Range
    sheet.clearSelection();
    sheet.addSelection(1, 2, numForecasts / 2 + 1, forecasts[0].TimeSeries[0].StableBalance.length + 3);
    const runOffLookupFormula = GC.Spread.Sheets.CalcEngine.rangeToFormula(sheet.getSelections()[0]);
    sheet.addCustomName('EMP_BSTargets', `'${sheet.name()}'!${runOffLookupFormula}`, 1, 1, '');
    sheet.clearSelection();

    // Setting headers for stable balance
    rowIndex++;
    sheet.getCell(rowIndex, 1).text('Mnemonic');
    sheet.getCell(rowIndex, 2).text('sBalTargets');
    sheet.getCell(rowIndex, 3).text('Entity');
    sheet.getCell(rowIndex, 4).text('Target Method');
    sheet.setArray(rowIndex, 5, [new Array(forecasts[0].TimeSeries[0].StableBalance.length).fill(0).map((val, ind) => (val = ind))]);

    // Stable Balance Loop
    forecasts.forEach((forecast, index) => {
      forecast.TimeSeries.forEach(timeSeries => {
        rowIndex++;
        sheet.getCell(rowIndex, 1).text(timeSeries.Mnemonic);
        sheet.getCell(rowIndex, 2).text(String(timeSeries.AccountNum));
        sheet.getCell(rowIndex, 3).text(timeSeries.EntityID !== null ? timeSeries.EntityID : '');
        sheet.getCell(rowIndex, 4).text('Average');
        sheet.setArray(rowIndex, 5, [timeSeries.StableBalance.map(({ Balance }) => Balance)]);
      });
    });

    const stableBalRow = numForecasts / 2 + 5;

    sheet.addSelection(stableBalRow, 2, numForecasts / 2 + 1, forecasts[0].TimeSeries[0].StableBalance.length + 3);
    const forecastStableBalanceFormula = GC.Spread.Sheets.CalcEngine.rangeToFormula(sheet.getSelections()[0]);
    sheet.addCustomName('EMP_sBalTargets', `'${sheet.name()}'!${forecastStableBalanceFormula}`, stableBalRow, 1, '');
    sheet.clearSelection();
  }

  setStyling_Forecasts(sheet, forecasts);

  return sheet;
};

const setStyling_Forecasts = (sheet: GC.Spread.Sheets.Worksheet, forecasts: IALMForecast[]): void => {
  if (sheet && forecasts.length > 0 && forecasts[0].TimeSeries.length > 0 && forecasts[0].TimeSeries[0].StableBalance.length > 0) {
    sheet.setColumnWidth(0, 20);
    sheet.setColumnWidth(1, 120);
    sheet.setColumnWidth(2, 120);
    sheet.setColumnWidth(3, 120);
    const startRowHeaders = 0;
    let startRowTotalBal = 2;
    const startColValues = 5;

    let startRowStableBal = 0;
    forecasts.forEach(x => {
      startRowStableBal += x.TimeSeries.length;
    });
    startRowStableBal += 4;

    let numForecasts = 0;
    forecasts.forEach(x => {
      numForecasts += x.TimeSeries.length;
    });
    const numPeriods = forecasts[0].TimeSeries[0].StableBalance.length;
    const entireSheet = sheet.getRange(startRowHeaders, 1, sheet.getRowCount(), sheet.getColumnCount());
    entireSheet
      .locked(true)
      .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center)
      .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`)
      .wordWrap(true)
      .font(`${parseInt(FONT_SIZE, 10)}pt ${FONT_NAME}`);
    // Total Balance headers

    const totalBalanceHeaders = sheet.getRange(1, 1, 1, numPeriods + 4);
    totalBalanceHeaders
      .backColor(colours.Lookups.columnBackground)
      .font(`bold ${Number(FONT_SIZE) + 2}pt ${FONT_NAME}`)
      .setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });

    // Adding alternating black and white styling total bal
    for (let c = 0; c < numForecasts; c++) {
      sheet.getRange(2 + c, 1, 1, numPeriods).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
    }

    sheet.getRange(2, 1, numForecasts, 4).setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
    sheet.getRange(2, numPeriods + 4, numForecasts, 1).borderRight(borders.BLACK_THIN);

    // Stable Balance Header Styling
    startRowStableBal++;
    const stableBalanceHeaders = sheet.getRange(startRowStableBal, 1, 1, numPeriods + 4);
    stableBalanceHeaders
      .backColor(colours.Lookups.columnBackground)
      .font(`bold ${Number(FONT_SIZE) + 2}pt ${FONT_NAME}`)
      .setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
    sheet.getRange(startRowStableBal + 1, 1, numForecasts, 4).setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
    // Adding alternating black and white styling stable bal
    for (let c = 0; c < numForecasts; c++) {
      sheet.getRange(startRowStableBal + c + 1, 1, 1, numPeriods).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
    }

    sheet.getRange(startRowStableBal + 1, numPeriods + 4, numForecasts, 1).borderRight(borders.BLACK_THIN);
    startRowStableBal++;

    // Adding formatters
    // Total Balance
    sheet.getRange(startRowTotalBal, startColValues, numForecasts, numPeriods).formatter(CURRENCY_FORMATTER);
    // Stable Balance
    sheet.getRange(startRowStableBal, startColValues, numForecasts, numPeriods).formatter(CURRENCY_FORMATTER);

    // Making column 0 light grey
    // Total Bal
    sheet.getRange(startRowTotalBal, startColValues, numForecasts, numPeriods).backColor(colours.LIGHT_GREY);
    // StableBal
    sheet.getRange(startRowStableBal, startColValues, numForecasts, numPeriods).backColor(colours.LIGHT_GREY);

    // Balances Styling

    forecasts.forEach(forecast => {
      forecast.TimeSeries.forEach(timeSeries => {
        const allDates = timeSeries.StableBalance.map(({ PeriodEndDate }) => PeriodEndDate.substr(0, 7));
        const lastObservationDateStr = getLastDayOfNextMonth(new Date(forecast.LastObservationDate))
          .toJSON()
          .substr(0, 7);

        // console.log(`Time Series: ${timeSeries.Mnemonic} Last OBS Date ${lastObservationDateStr}`);

        const lastObservationDateIndex = allDates.findIndex(x => x === lastObservationDateStr);

        if (lastObservationDateIndex >= 0) {
          // Total Balance Green Area
          const greenAreaTotalBalance = sheet.getRange(
            startRowTotalBal,
            startColValues + lastObservationDateIndex,
            1,
            numPeriods - lastObservationDateIndex
          );
          greenAreaTotalBalance.backColor(colours.LIGHT_GREEN);
          // Stable Balance Green Area
          const greenAreaStableBalance = sheet.getRange(
            startRowStableBal,
            startColValues + lastObservationDateIndex,
            1,
            numPeriods - lastObservationDateIndex
          );

          greenAreaStableBalance.backColor(colours.LIGHT_GREEN);
        } else if (new Date(getLastDayOfNextMonth(new Date(forecast.CalibrationEndDate))) < new Date(allDates[0])) {
          // Forecast Date is before set entire area to green

          const greenAreaTotalBalance = sheet.getRange(startRowTotalBal, startColValues, 1, numPeriods);
          greenAreaTotalBalance.backColor(colours.LIGHT_GREEN);

          // Stable Balance
          const greenAreaStableBalance = sheet.getRange(startRowStableBal, startColValues, 1, numPeriods);

          greenAreaStableBalance.backColor(colours.LIGHT_GREEN);
        }

        // Making column 0 light grey
        // Total Bal
        sheet.getRange(startRowTotalBal, startColValues, 1, 1).backColor(colours.LIGHT_GREY);
        // StableBal
        sheet.getRange(startRowStableBal, startColValues, 1, 1).backColor(colours.LIGHT_GREY);
        startRowStableBal++;
        startRowTotalBal++;
      });
    });

    // Bottom borders for each range
    sheet.getRange(startRowTotalBal, 1, 1, numPeriods + 4).borderTop(borders.BLACK_THIN);
    sheet.getRange(startRowStableBal, 1, 1, numPeriods + 4).borderTop(borders.BLACK_THIN);

    // Hiding entity id column
    if (!hasEntityId(sheet, numForecasts)) {
      sheet.deleteColumns(3, 1);
    }
  }
};

const hasEntityId = (
  sheet: GC.Spread.Sheets.Worksheet,

  numForecasts: number
): boolean => {
  const allEntityIds = flat(sheet.getArray(2, 3, numForecasts, 1)).filter(x => x !== '');
  return allEntityIds.length === 0 ? false : true;
};
